/**
 * Panneau latéral de détail/édition d'une tâche : titre, description, statut, priorité,
 * assigné, dates, charge, avancement, étiquettes, dépendances, commentaires.
 *
 * Fermeture par Échap et par clic extérieur (design system §11). Chaque champ édite en
 * inline (`onBlur`/`onChange` → `PATCH`), sans bouton « enregistrer » séparé.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEPENDENCY_TYPES,
  TASK_KINDS,
  TASK_PRIORITIES,
  TASK_STATUSES,
} from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert, appConfirm } from '../dialogs/service.js';
import {
  LoadError,
  TruncatedListWarning,
  taskReference,
  labelForKind,
  labelForPriority,
  labelForStatus,
  StatusTag,
} from './common.jsx';
import {
  BTN_GHOST_SM,
  BTN_ICON,
  FORM_FIELD,
  FORM_LABEL,
  INPUT,
  INPUT_SM,
  SELECT_SM,
  TAG,
  TEXTAREA,
} from '../ui/theme.js';

export function TaskDrawer({
  projectId,
  projectKey,
  taskId,
  canEdit = false,
  onCreateSubtask = null,
  onClose,
  onChanged,
}) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const [comment, setComment] = useState('');
  const [newDependency, setNewDependency] = useState({
    predecessorId: '',
    type: 'FS',
    lagDays: '',
  });

  const {
    data: task,
    error: taskError,
    patch,
    reload,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}/tasks/${taskId}`, { signal }).then((r) => r.data),
    [projectId, taskId],
  );
  const {
    data: commentsPage,
    error: commentsError,
    reload: reloadComments,
  } = useAsync(
    (signal) => fetchAllPages(`/projects/${projectId}/tasks/${taskId}/comments`, { signal }),
    [projectId, taskId],
  );
  const comments = commentsPage?.data;
  /*
   * Toutes les tâches du projet, chargées une fois : elles servent au choix du lot parent
   * et, plus bas, au choix du prédécesseur d'une dépendance. Aucune route nouvelle.
   */
  const {
    data: projectTasksPage,
    error: projectTasksError,
    reload: reloadProjectTasks,
  } = useAsync((signal) => fetchAllPages(`/projects/${projectId}/tasks`, { signal }), [projectId]);
  const projectTasks = projectTasksPage?.data;
  /*
   * Membres du PROJET (pas de l'équipe), même route que `ShareProjectDialog` — c'est la bonne
   * granularité d'accès. Le serveur restreint aujourd'hui l'affectation aux membres d'ÉQUIPE
   * (issue #71, traitée en parallèle) : ce panneau écrit pour la cible, sans filtrage
   * défensif — une troisième définition de « qui a accès » ne ferait qu'ajouter un désaccord.
   */
  const {
    data: membersPage,
    error: membersError,
    reload: reloadMembers,
  } = useAsync(
    (signal) => fetchAllPages(`/projects/${projectId}/members`, { signal }),
    [projectId],
  );
  const members = membersPage?.data;

  // Les listes annexes (lots, prédécesseurs, responsables) échouent sans bloquer la tâche : on
  // le dit et on ne relance que ce qui a échoué.
  const annexError = projectTasksError ?? membersError;
  const retryAnnex = () => {
    if (projectTasksError) reloadProjectTasks();
    if (membersError) reloadMembers();
  };

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }
    function onClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [onClose]);

  async function save(fields) {
    patch((current) => ({ ...current, ...fields }));
    try {
      await api.patch(`/projects/${projectId}/tasks/${taskId}`, fields);
      onChanged?.();
    } catch (error) {
      reload();
      await appAlert(t('taskDrawer.updateFailed', { message: error.message }));
    }
  }

  async function removeDependency(depId) {
    if (!(await appConfirm(t('taskDrawer.removeDependencyConfirm')))) return;
    try {
      await api.delete(`/projects/${projectId}/tasks/${taskId}/dependencies/${depId}`);
      reload();
      onChanged?.();
    } catch (error) {
      await appAlert(t('taskDrawer.removeDependencyFailed', { message: error.message }));
    }
  }

  /**
   * La tâche ouverte est toujours le **successeur** : le champ demande « de quoi cette tâche
   * dépend-elle ». Poser les deux sens dans un seul formulaire obligerait à un quatrième
   * contrôle pour un besoin qui se règle en ouvrant l'autre tâche.
   */
  async function addDependency(event) {
    event.preventDefault();
    if (!newDependency.predecessorId) return;
    try {
      await api.post(`/projects/${projectId}/tasks/${taskId}/dependencies`, {
        predecessorId: newDependency.predecessorId,
        successorId: taskId,
        type: newDependency.type,
        lagDays: Number(newDependency.lagDays) || 0,
      });
      setNewDependency({ predecessorId: '', type: 'FS', lagDays: '' });
      reload();
      onChanged?.();
    } catch (error) {
      await appAlert(t('taskDrawer.addDependencyFailed', { message: error.message }));
    }
  }

  /**
   * Un lot est un conteneur pur (D1) : ses dates, ses charges, son avancement et son statut
   * viennent de ses tâches. Le serveur les ignore s'ils sont envoyés — les proposer en saisie
   * offrirait de renseigner une valeur qui disparaît au rechargement, sans un mot.
   */
  const estUnLot = task?.kind === 'phase';

  async function submitComment(event) {
    event.preventDefault();
    if (!comment.trim()) return;
    try {
      await api.post(`/projects/${projectId}/tasks/${taskId}/comments`, { body: comment.trim() });
      setComment('');
      reloadComments();
    } catch (error) {
      await appAlert(t('taskDrawer.commentRejected', { message: error.message }));
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-overlay">
      <aside
        ref={panelRef}
        role="dialog"
        aria-label={t('taskDrawer.ariaLabel')}
        className="flex h-full w-full max-w-md flex-col overflow-y-auto border-l-2 border-line bg-paper p-4"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-faint">
            {task ? taskReference(projectKey ?? '…', task.number) : '…'}
          </span>
          <button
            type="button"
            className={BTN_ICON}
            onClick={onClose}
            aria-label={t('taskDrawer.close')}
          >
            ✕
          </button>
        </div>

        {!task ? (
          taskError ? (
            <LoadError error={taskError} onRetry={reload} className="mt-4" />
          ) : (
            <p className="mt-4 text-sm text-ink-soft">{t('taskDrawer.loading')}</p>
          )
        ) : (
          <>
            {annexError && <LoadError error={annexError} onRetry={retryAnnex} className="mt-3" />}
            {(projectTasksPage?.truncated || membersPage?.truncated) && (
              <div className="mt-3">
                <TruncatedListWarning />
              </div>
            )}
            <input
              // Gabarit INPUT intact (reference.md §6.0) : ni text-base ni py-* en surcharge,
              // le titre se distingue par sa graisse seule.
              className={`${INPUT} mt-3 w-full font-semibold`}
              defaultValue={task.title}
              aria-label={t('taskDrawer.titleAria')}
              onBlur={(e) => e.target.value !== task.title && save({ title: e.target.value })}
            />

            <textarea
              className={`${TEXTAREA} mt-2 w-full`}
              rows={4}
              placeholder={t('taskDrawer.descriptionPlaceholder')}
              defaultValue={task.description ?? ''}
              aria-label={t('taskDrawer.descriptionAria')}
              onBlur={(e) => save({ description: e.target.value })}
            />

            <div className="mt-3 grid grid-cols-2 gap-2">
              {!estUnLot && (
                <label className={FORM_FIELD}>
                  <span className={FORM_LABEL}>{t('taskDrawer.status')}</span>
                  <select
                    className={SELECT_SM}
                    value={task.status}
                    onChange={(e) => save({ status: e.target.value })}
                  >
                    {TASK_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {labelForStatus(s, t)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className={FORM_FIELD}>
                <span className={FORM_LABEL}>{t('taskDrawer.priority')}</span>
                <select
                  className={SELECT_SM}
                  value={task.priority}
                  onChange={(e) => save({ priority: e.target.value })}
                >
                  {TASK_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {labelForPriority(p, t)}
                    </option>
                  ))}
                </select>
              </label>

              <label className={FORM_FIELD}>
                <span className={FORM_LABEL}>{t('taskDrawer.kind')}</span>
                <select
                  className={SELECT_SM}
                  value={task.kind}
                  onChange={(e) => save({ kind: e.target.value })}
                >
                  {TASK_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {labelForKind(k, t)}
                    </option>
                  ))}
                </select>
              </label>

              <label className={`${FORM_FIELD} col-span-2`}>
                {/*
                 * Surface ajoutée par ce lot : elle honore canEdit, contrairement aux
                 * champs plus anciens du panneau (statut, priorité…) qui ne le font pas
                 * encore — incohérence antérieure, traitée hors de ce lot.
                 */}
                <span className={FORM_LABEL}>{t('taskDrawer.parent')}</span>
                <select
                  className={SELECT_SM}
                  value={task.parentId ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => save({ parentId: e.target.value || null })}
                >
                  <option value="">{t('taskDrawer.noParent')}</option>
                  {(projectTasks ?? [])
                    .filter((entry) => entry.kind === 'phase' && entry.id !== task.id)
                    .map((phase) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.title}
                      </option>
                    ))}
                </select>
              </label>

              {/*
               * Le responsable n'est PAS un champ dérivé (§CHAMPS_DERIVES côté serveur) : il
               * reste actif même sur un lot, contrairement à statut/dates/charge/avancement
               * ci-dessous.
               */}
              <label className={`${FORM_FIELD} col-span-2`}>
                <span className={FORM_LABEL}>{t('taskDrawer.assignee')}</span>
                <select
                  className={SELECT_SM}
                  value={task.assigneeId ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => save({ assigneeId: e.target.value || null })}
                >
                  <option value="">{t('taskDrawer.noAssignee')}</option>
                  {(members ?? []).map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.name || member.email}
                    </option>
                  ))}
                </select>
              </label>

              {!estUnLot && (
                <>
                  <label className={FORM_FIELD}>
                    <span className={FORM_LABEL}>{t('taskDrawer.estimateHours')}</span>
                    <input
                      type="number"
                      min="0"
                      className={INPUT_SM}
                      defaultValue={task.estimateHours ?? ''}
                      onBlur={(e) =>
                        save({ estimateHours: e.target.value ? Number(e.target.value) : null })
                      }
                    />
                  </label>

                  <label className={FORM_FIELD}>
                    <span className={FORM_LABEL}>{t('taskDrawer.startDate')}</span>
                    <input
                      type="date"
                      className={INPUT_SM}
                      defaultValue={task.startDate ?? ''}
                      onChange={(e) => save({ startDate: e.target.value || null })}
                    />
                  </label>

                  <label className={FORM_FIELD}>
                    <span className={FORM_LABEL}>{t('taskDrawer.dueDate')}</span>
                    <input
                      type="date"
                      className={INPUT_SM}
                      defaultValue={task.dueDate ?? ''}
                      onChange={(e) => save({ dueDate: e.target.value || null })}
                    />
                  </label>

                  <label className={`${FORM_FIELD} col-span-2`}>
                    <span className={FORM_LABEL}>
                      {t('taskDrawer.progress', { value: task.progress ?? 0 })}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      defaultValue={task.progress ?? 0}
                      onChange={(e) => save({ progress: Number(e.target.value) })}
                    />
                  </label>
                </>
              )}
            </div>

            {estUnLot && (
              <div className="mt-3 border-2 border-line-soft p-2">
                <p className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                  {t('taskDrawer.derived')}
                </p>
                <dl className="mt-1 grid grid-cols-2 gap-1 text-sm">
                  <dt className="text-ink-soft">{t('taskDrawer.startDate')}</dt>
                  <dd>{task.startDate ?? '—'}</dd>
                  <dt className="text-ink-soft">{t('taskDrawer.dueDate')}</dt>
                  <dd>{task.dueDate ?? '—'}</dd>
                  <dt className="text-ink-soft">{t('taskDrawer.estimateHours')}</dt>
                  <dd>{task.estimateHours ?? '—'}</dd>
                  <dt className="text-ink-soft">{t('taskDrawer.status')}</dt>
                  <dd>
                    <StatusTag status={task.status} />
                  </dd>
                  <dt className="col-span-2 text-ink-soft">
                    {t('taskDrawer.progress', { value: task.progress ?? 0 })}
                  </dt>
                </dl>
              </div>
            )}

            {onCreateSubtask && task.kind === 'phase' && (
              <button
                type="button"
                className={`${BTN_GHOST_SM} mt-3 self-start`}
                onClick={() => onCreateSubtask(task.id)}
              >
                {t('taskDrawer.addSubtask')}
              </button>
            )}

            <div className="mt-4">
              <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-faint">
                {t('taskDrawer.labels')}
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(task.labels ?? []).map((label) => (
                  <span key={label.id} className={`${TAG} border-line text-ink-soft`}>
                    {label.name}
                  </span>
                ))}
                {(task.labels ?? []).length === 0 && (
                  <span className="text-xs text-ink-faint">{t('taskDrawer.noLabels')}</span>
                )}
              </div>
            </div>

            <div className="mt-4">
              <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-faint">
                {t('taskDrawer.dependencies')}
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {/*
                 * L'API renvoie deux tableaux distincts — `incoming` (cette tâche est le
                 * successeur) et `outgoing` (elle est le prédécesseur). Le sens est porté par
                 * un libellé, « dépend de » ou « bloque », jamais par la seule position.
                 */}
                {[
                  ...(task.incoming ?? []).map((dep) => ({
                    dep,
                    otherId: dep.predecessorId,
                    wayKey: 'taskDrawer.dependsOn',
                  })),
                  ...(task.outgoing ?? []).map((dep) => ({
                    dep,
                    otherId: dep.successorId,
                    wayKey: 'taskDrawer.blocks',
                  })),
                ].map(({ dep, otherId, wayKey }) => {
                  const other = (projectTasks ?? []).find((entry) => entry.id === otherId);
                  return (
                    <li
                      key={dep.id}
                      className="flex items-center justify-between gap-2 text-xs text-ink-soft"
                    >
                      <span>
                        <span className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                          {t(wayKey)} · {dep.type}
                          {dep.lagDays
                            ? ` · ${t('taskDrawer.dependencyLag', { days: dep.lagDays })}`
                            : ''}
                        </span>{' '}
                        {other
                          ? `${taskReference(projectKey ?? '…', other.number)} ${other.title}`
                          : otherId}
                      </span>
                      {canEdit && (
                        <button
                          type="button"
                          className={BTN_GHOST_SM}
                          onClick={() => removeDependency(dep.id)}
                        >
                          {t('common.remove')}
                        </button>
                      )}
                    </li>
                  );
                })}
                {(task.incoming ?? []).length + (task.outgoing ?? []).length === 0 && (
                  <li className="text-xs text-ink-faint">{t('taskDrawer.noDependencies')}</li>
                )}
              </ul>

              {canEdit && (
                <form onSubmit={addDependency} className="mt-2 flex flex-wrap items-center gap-2">
                  <select
                    className={`${SELECT_SM} flex-1`}
                    aria-label={t('taskDrawer.dependencyPredecessorAria')}
                    value={newDependency.predecessorId}
                    onChange={(e) =>
                      setNewDependency((current) => ({ ...current, predecessorId: e.target.value }))
                    }
                  >
                    <option value="">{t('taskDrawer.dependencyPlaceholder')}</option>
                    {(projectTasks ?? [])
                      .filter((entry) => entry.id !== task.id)
                      .map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {`${taskReference(projectKey ?? '…', entry.number)} ${entry.title}`}
                        </option>
                      ))}
                  </select>
                  <select
                    className={SELECT_SM}
                    aria-label={t('taskDrawer.dependencyTypeAria')}
                    value={newDependency.type}
                    onChange={(e) =>
                      setNewDependency((current) => ({ ...current, type: e.target.value }))
                    }
                  >
                    {DEPENDENCY_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    className={`${INPUT_SM} w-16`}
                    aria-label={t('taskDrawer.dependencyLagAria')}
                    value={newDependency.lagDays}
                    onChange={(e) =>
                      setNewDependency((current) => ({ ...current, lagDays: e.target.value }))
                    }
                  />
                  <button type="submit" className={BTN_GHOST_SM}>
                    {t('taskDrawer.addDependency')}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-4 flex flex-1 flex-col">
              <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-faint">
                {t('taskDrawer.comments')}
              </p>
              {commentsError && (
                <LoadError error={commentsError} onRetry={reloadComments} className="mt-1" />
              )}
              {commentsPage?.truncated && (
                <div className="mt-1">
                  <TruncatedListWarning />
                </div>
              )}
              <ul className="mt-1 flex flex-col gap-2">
                {(comments ?? []).map((c) => (
                  <li key={c.id} className="border-2 border-line-soft p-2 text-xs text-ink-soft">
                    <span className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
                      {c.author?.name ?? t('common.anonymous')}
                    </span>
                    <p className="mt-1">{c.body}</p>
                  </li>
                ))}
              </ul>
              <form onSubmit={submitComment} className="mt-2 flex gap-2">
                <input
                  className={`${INPUT_SM} flex-1`}
                  placeholder={t('taskDrawer.addCommentPlaceholder')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  aria-label={t('taskDrawer.newCommentAria')}
                />
                <button type="submit" className={BTN_GHOST_SM}>
                  {t('common.send')}
                </button>
              </form>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
